import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  HStack,
  VStack,
  Box,
  Icon,
  Progress,
  Image,
  AspectRatio,
  Portal,
  CloseButton,
} from "@chakra-ui/react";
import { FiHelpCircle, FiChevronLeft, FiChevronRight, FiCheckCircle } from "react-icons/fi";

export default function Tutorial({ isOpen, onClose, steps: providedSteps }) {
  const defaultSteps = useMemo(
    () => [
      {
        title: "Hoş geldiniz",
        description:
          "Bu kısa tur, ana menü ve önemli sayfaları hızlıca tanıtır.",
      },
      {
        title: "Yönetim menüsü",
        description:
          "Personel, müşteri ve proje gibi yönetim sayfalarına buradan ulaşabilirsiniz.",
      },
      {
        title: "İşlemler",
        description:
          "İş emirleri, görevler ve randevulara erişim için işlemler menüsünü kullanın.",
      },
      {
        title: "Karanlık mod ve bilgiler",
        description:
          "Üst çubuktan karanlık modu açabilir, bilgi çubuğunu görüntüleyebilirsiniz.",
      },
    ],
    []
  );
  const steps = providedSteps && providedSteps.length > 0 ? providedSteps : defaultSteps;

  const [index, setIndex] = useState(0);
  const isFirst = index === 0;
  const isLast = index === steps.length - 1;
  const [targetRect, setTargetRect] = useState(null);

  const next = () => setIndex((i) => Math.min(i + 1, steps.length - 1));
  const prev = () => setIndex((i) => Math.max(i - 1, 0));
  const finish = () => {
    onClose?.();
    setIndex(0);
    setTargetRect(null);
  };

  const progress = Math.round(((index + 1) / steps.length) * 100);

  // Locate target element for coach marks if selector is provided
  useEffect(() => {
    if (!isOpen) return;
    const step = steps[index] || {};
    const selector = step.selector;
    const dataId = step.dataId;
    let el = null;
    if (selector) {
      try { el = document.querySelector(selector); } catch (e) { el = null; }
    }
    if (!el && dataId) {
      el = document.querySelector(`[data-tour-id="${dataId}"]`);
    }

    if (el) {
      // Scroll into view and set rect
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      } catch (_e) {}
      const updateRect = () => {
        const r = el.getBoundingClientRect();
        setTargetRect({
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
          bottom: r.bottom,
          right: r.right,
        });
      };
      updateRect();
      const handle = () => updateRect();
      window.addEventListener('resize', handle);
      window.addEventListener('scroll', handle, true);
      return () => {
        window.removeEventListener('resize', handle);
        window.removeEventListener('scroll', handle, true);
      };
    } else {
      setTargetRect(null);
      return undefined;
    }
  }, [isOpen, index, steps]);

  const hasTarget = !!targetRect;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 360;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 640;
  const cardLeft = hasTarget ? Math.max(8, Math.min(targetRect.left, vw - 360 - 8)) : 0;
  const cardTop = hasTarget ? (targetRect.bottom + 8) : 0;

  return (
    <>
      {/* Selector-based Coach Marks */}
      {isOpen && hasTarget && (
        <Portal>
          {/* Dim background outside target (spotlight effect) */}
          {/* Top overlay */}
          <Box position="fixed" left="0px" top="0px" width={`${vw}px`} height={`${Math.max(0, targetRect.top)}px`} bg="blackAlpha.600" zIndex={1500} />
          {/* Left overlay */}
          <Box position="fixed" left="0px" top={`${targetRect.top}px`} width={`${Math.max(0, targetRect.left)}px`} height={`${targetRect.height}px`} bg="blackAlpha.600" zIndex={1500} />
          {/* Right overlay */}
          <Box position="fixed" left={`${targetRect.right}px`} top={`${targetRect.top}px`} width={`${Math.max(0, vw - targetRect.right)}px`} height={`${targetRect.height}px`} bg="blackAlpha.600" zIndex={1500} />
          {/* Bottom overlay */}
          <Box position="fixed" left="0px" top={`${targetRect.bottom}px`} width={`${vw}px`} height={`${Math.max(0, vh - targetRect.bottom)}px`} bg="blackAlpha.600" zIndex={1500} />

          {/* Highlight box over the target element */}
          <Box
            position="fixed"
            zIndex={1501}
            left={`${targetRect.left}px`}
            top={`${targetRect.top}px`}
            width={`${targetRect.width}px`}
            height={`${targetRect.height}px`}
            
            boxShadow="0 0 0 3px rgba(66, 153, 225, 0.4)"
            pointerEvents="none"
          />

          {/* Coach card */}
          <Box
            position="fixed"
            zIndex={1502}
            left={`${cardLeft}px`}
            top={`${cardTop}px`}
            width={{ base: '92vw', md: '360px' }}
            maxW="360px"
            bg="white"
            borderRadius="md"
            boxShadow="xl"
            borderWidth="1px"
            borderColor="gray.200"
            p={4}
          >
            <HStack justify="space-between" mb={2}>
              <HStack spacing={3}>
                <Icon as={FiHelpCircle} color="blue.500" />
                <Text fontWeight="semibold">Öğretici</Text>
              </HStack>
              <CloseButton onClick={finish} />
            </HStack>

            <Box>
              <Progress value={progress} size="sm" colorScheme="blue" borderRadius="md" />
              <HStack justify="space-between" mt={2}>
                <Text fontWeight="semibold">{steps[index].title}</Text>
                <Text fontSize="sm" color="gray.500">
                  {index + 1}/{steps.length}
                </Text>
              </HStack>
            </Box>

            {steps[index].image && (
              <Box borderWidth="1px" borderRadius="md" overflow="hidden" bg="gray.50" mt={3}>
                <AspectRatio ratio={16 / 9}>
                  <Image src={steps[index].image} alt={steps[index].title} objectFit="cover" />
                </AspectRatio>
              </Box>
            )}

            <Text color="gray.700" mt={3}>{steps[index].description}</Text>

            <HStack spacing={2} mt={4} justify="flex-end">
              <Button leftIcon={<FiChevronLeft />} onClick={prev} isDisabled={isFirst} variant="outline">
                Geri
              </Button>
              {!isLast ? (
                <Button rightIcon={<FiChevronRight />} colorScheme="blue" onClick={next}>
                  İleri
                </Button>
              ) : (
                <Button leftIcon={<FiCheckCircle />} colorScheme="green" onClick={finish}>
                  Bitir
                </Button>
              )}
            </HStack>
          </Box>
        </Portal>
      )}

      {/* Modal fallback for non-targeted steps */}
      {isOpen && !hasTarget && (
        <Modal isOpen={isOpen} onClose={finish} size="lg" isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <HStack spacing={3}>
                <Icon as={FiHelpCircle} color="blue.500" />
                <Text>Öğretici</Text>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Progress value={progress} size="sm" colorScheme="blue" borderRadius="md" />
                  <HStack justify="space-between" mt={2}>
                    <Text fontWeight="semibold">{steps[index].title}</Text>
                    <Text fontSize="sm" color="gray.500">
                      {index + 1}/{steps.length}
                    </Text>
                  </HStack>
                </Box>
                {steps[index].image && (
                  <Box borderWidth="1px" borderRadius="md" overflow="hidden" bg="gray.50">
                    <AspectRatio ratio={16 / 9}>
                      <Image
                        src={steps[index].image}
                        alt={steps[index].title}
                        objectFit="cover"
                      />
                    </AspectRatio>
                  </Box>
                )}
                <Text color="gray.700">{steps[index].description}</Text>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <HStack spacing={2}>
                <Button
                  leftIcon={<FiChevronLeft />}
                  onClick={prev}
                  isDisabled={isFirst}
                  variant="outline"
                >
                  Geri
                </Button>
                {!isLast ? (
                  <Button rightIcon={<FiChevronRight />} colorScheme="blue" onClick={next}>
                    İleri
                  </Button>
                ) : (
                  <Button leftIcon={<FiCheckCircle />} colorScheme="green" onClick={finish}>
                    Bitir
                  </Button>
                )}
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}